<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { IG_TEMPLATE_TYPES } from 'dashboard/helper/instagramTemplateHelper';

const props = defineProps({
  inboxId: { type: Number, default: undefined },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();

const allTemplates = useMapGetter('messageTemplates/getMessageTemplates');
const uiFlags = useMapGetter('messageTemplates/getUIFlags');

const isFetching = computed(() => uiFlags.value.isFetching);

// Instagram templates live in the message_templates table rather than on the
// channel, so they come from the API and not from inboxes/getWhatsAppTemplates.
const templates = computed(() =>
  (allTemplates.value || []).filter(
    template =>
      template.channel_type === 'Channel::Instagram' &&
      template.inbox_id === props.inboxId
  )
);

const typeLabel = template => {
  const type = template.content?.template_type || IG_TEMPLATE_TYPES.GENERIC;
  return t(`MESSAGE_TEMPLATES.INSTAGRAM.TYPES.${type.toUpperCase()}`);
};

const summary = template => {
  const content = template.content || {};
  return content.text || content.elements?.[0]?.title || '';
};

onMounted(() => {
  store.dispatch('messageTemplates/get', {
    inbox_id: props.inboxId,
    channel_type: 'Channel::Instagram',
  });
});
</script>

<template>
  <div class="w-full">
    <div v-if="isFetching" class="flex items-center justify-center py-10">
      <Spinner />
    </div>
    <div v-else-if="!templates.length" class="py-8 text-center text-n-slate-11">
      {{ t('INSTAGRAM_TEMPLATES.MODAL.EMPTY') }}
    </div>
    <div v-else class="flex flex-col divide-y divide-n-weak">
      <button
        v-for="template in templates"
        :key="template.id"
        type="button"
        class="flex flex-col items-start gap-1 px-3 py-3 -mx-3 text-left rounded-lg hover:bg-n-alpha-1"
        @click="emit('onSelect', template)"
      >
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </span>
          <span
            class="px-2 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ typeLabel(template) }}
          </span>
        </div>
        <span v-if="summary(template)" class="text-xs truncate text-n-slate-11">
          {{ summary(template) }}
        </span>
      </button>
    </div>
  </div>
</template>
